import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { createColumnHelper } from "@tanstack/react-table";
import { usePaged } from "@/lib/hooks";
import { PagedTable } from "@/components/PagedTable";
import type { AuditLogRow } from "@/lib/types";
import { formatCompanyDateTime } from "@/lib/time";

const col = createColumnHelper<AuditLogRow>();

export default function AuditLogPage() {
  const { t } = useTranslation();
  const paged = usePaged<AuditLogRow>(["audit-logs"], "/audit-logs");

  const columns = useMemo(
    () => [
      col.accessor("created_at", {
        header: () => t("audit.when"),
        cell: (c) => formatCompanyDateTime(c.getValue()),
      }),
      col.accessor("action", {
        header: () => t("audit.action"),
        cell: (c) => <span className="font-mono text-xs">{c.getValue()}</span>,
      }),
      col.accessor("entity_type", {
        header: () => t("audit.entity"),
        cell: (c) => (
          <span className="text-xs">
            {c.getValue()}
            {c.row.original.entity_id ? (
              <span className="text-sand-muted"> · {c.row.original.entity_id.slice(0, 8)}</span>
            ) : null}
          </span>
        ),
      }),
      col.accessor("actor_channel", { header: () => t("audit.channel") }),
      col.accessor("actor_user_id", {
        header: () => t("audit.actor"),
        cell: (c) => (c.getValue() ? c.getValue()!.slice(0, 8) : "—"),
      }),
    ],
    [t],
  );

  return (
    <div className="space-y-4">
      <h1 className="text-lg font-semibold">{t("audit.title")}</h1>
      <PagedTable
        columns={columns}
        data={paged.items}
        query={paged.query}
        total={paged.total}
        page={paged.page}
        pageCount={paged.pageCount}
        onPage={paged.setPage}
        empty={t("audit.empty")}
      />
    </div>
  );
}
